import { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import produce from 'immer'
import {
  Modal as AntModal,
  Button,
  Form as AntForm,
  Row,
  notification,
  Typography,
  Switch
} from 'antd'
import { useForm, useFieldArray, FormProvider } from 'react-hook-form'
import { useNavigate, generatePath } from 'react-router-dom'
import * as ROUTES from 'admin/routes'
import { useApi, useApiQuery } from 'admin/lib/Api'
import { moduleNameFromId } from 'admin/lib/constants'
import { Guid } from 'admin/lib/guid'
import { Section, SectionProvider, FormItem, Select } from 'admin/lib/Forms'
import { BodyContentColumn } from 'admin/lib/Layouts'
import Password from 'antd/lib/input/Password'
import { MODULE_ID, PROVIDER_DEFINITION_ID } from '@qflow/constants'
import * as uuid from 'uuid'

export function EditConnection({
  connection,
  instruments,
  isNew,
  accountId,
  projectId
}) {
  const navigate = useNavigate()
  const [submitting, setSubmitting] = useState(false)

  const allInstruments = useApiQuery(
    useCallback(
      api => {
        return api.getAllConnectionInstruments(accountId, projectId)
      },
      [accountId, projectId]
    )
  )

  const allEmailAddresses = allInstruments.data
    ?.filter(x => x.projectId !== projectId)
    .map(x => x.email)
  const allEmailIdentifiers = allInstruments.data
    ?.filter(x => x.projectId !== projectId)
    .map(x => x.emailIdentifier)

  const assembleDraftItem = (connection, instruments) => {
    return {
      name: connection.name,
      providerDefinitionId: connection.providerDefinitionId,
      connectionId: connection.connectionId,
      connUsername:
        Array.isArray(connection.incomingCredentials) &&
        connection.incomingCredentials.length > 0
          ? connection.incomingCredentials[0].userName
          : uuid.v4(),
      connPassword:
        Array.isArray(connection.incomingCredentials) &&
        connection.incomingCredentials.length > 0
          ? connection.incomingCredentials[0].password
          : uuid.v4(),
      isChanged: false,
      emails: instruments.map(instrument => {
        return {
          email: instrument.externalId,
          isDeleted: false,
          emailIdentifier: instrument.feeds[0].externalId,
          moduleTagDefinitionId: instrument.feeds[0].moduleTagDefinitionId,
          permittedFileTypes:
            instrument.feeds[0].additionalFields.permittedFileTypes,
          permittedSendersPattern:
            instrument.feeds[0].additionalFields.permittedSendersPattern,
          feedId: instrument.feeds[0].feedId,
          instrumentId: instrument.instrumentId
        }
      })
    }
  }

  //
  // Form setup & values
  const draftItem = isNew
    ? blankConnection
    : assembleDraftItem(connection, instruments)

  const form = useForm({
    defaultValues: draftItem,
    mode: 'all',
    criteriaMode: 'firstError'
  })

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'emails'
  })

  const [isEmailForm, setIsEmailForm] = useState(
    draftItem.providerDefinitionId === PROVIDER_DEFINITION_ID.EMAIL_VALUE
  )

  const [isCredetialsChanged, setIsCredentialsChanged] = useState(isNew)

  //
  // Callbacks

  const api = useApi()
  const onSubmit = useCallback(
    async data => {
      setSubmitting(true)

      const submissionData = produce(draftItem, draft => {
        draft.name = data.name
        draft.connPassword = isEmailForm ? uuid.v4() : data.connPassword
        draft.connUsername = isEmailForm ? uuid.v4() : data.connUsername
        draft.providerDefinitionId = data.providerDefinitionId
        draft.isChanged = data.isChanged
      })

      function createEmailSubmissionData(connectionId, data) {
        if (!Array.isArray(data.emails)) {
          return []
        }

        const emails = data.emails

        const checkAllValuesAreDifferent = (arr, key) =>
          arr.every(
            (val, i, arr) => !arr[i + 1] || val[key] !== arr[i + 1][key]
          )

        if (!checkAllValuesAreDifferent(emails, 'email')) {
          setSubmitting(false)
          AntModal.error({
            title: 'Error Saving Connection',
            content: (
              <>
                <p>
                  Error, Please use a different email address for each
                  connection.
                </p>
                {emails.map(email => {
                  return (
                    <p>
                      {`Module: ${moduleNameFromId(
                        email.moduleTagDefinitionId
                      )}`}
                      <br />
                      {`Email: ${email.email}`}
                    </p>
                  )
                })}
              </>
            )
          })
          return null
        }

        if (!checkAllValuesAreDifferent(emails, 'emailIdentifier')) {
          setSubmitting(false)
          AntModal.error({
            title: 'Error Saving Connection',
            content: (
              <>
                <p>
                  Error, Please use a different Email Identifier for each
                  connection.
                </p>
                {emails.map(email => {
                  return (
                    <p>
                      {`Module: ${moduleNameFromId(
                        email.moduleTagDefinitionId
                      )}`}
                      <br />
                      {`Email: ${email.emailIdentifier}`}
                    </p>
                  )
                })}
              </>
            )
          })
          return null
        }

        if (!checkAllValuesAreDifferent(emails, 'moduleTagDefinitionId')) {
          setSubmitting(false)
          AntModal.error({
            title: 'Error Saving Connection',
            content: (
              <>
                <p>Error, Please use a different Module for each connection.</p>
                {emails.map(email => {
                  return (
                    <p>
                      {`Module: ${moduleNameFromId(
                        email.moduleTagDefinitionId
                      )}`}
                      <br />
                      {`Email: ${email.email}`}
                    </p>
                  )
                })}
              </>
            )
          })
          return null
        }

        return data.emails.map(email => {
          return {
            externalId: email.isDeleted
              ? `${email.email}#Deleted#${uuid.v4()}`
              : email.email,
            connectionId: connectionId,
            instrumentId: email.instrumentId,
            projectId: projectId,
            isDeleted: email.isDeleted,
            feeds: [
              {
                feedId: email.feedId,
                instrumentId: email.instrumentId,
                moduleTagDefinitionId: email.moduleTagDefinitionId,
                additionalFields: {
                  permittedFileTypes: email.permittedFileTypes,
                  permittedSendersPattern: email.permittedSendersPattern
                },
                externalId: email.isDeleted
                  ? `${email.emailIdentifier}#Deleted#${uuid.v4()}`
                  : email.emailIdentifier
              }
            ]
          }
        })
      }

      async function createNewEmailConnection(submissionData) {
        const [responseData, status, error] = await api.registerConnection(
          accountId,
          projectId,
          submissionData
        )

        if (error) {
          return [responseData, status, error]
        }

        const emailSubmissionData = createEmailSubmissionData(
          responseData.connectionId,
          data
        )

        if (!emailSubmissionData) {
          return [responseData, '', 'Connection Email Error']
        } else if (emailSubmissionData.length === 0) {
          return [responseData, status, error]
        }

        return await api.updateConnectionInstruments(
          accountId,
          projectId,
          responseData.connectionId,
          emailSubmissionData
        )
      }

      let status
      let error

      if (isNew) {
        if (isEmailForm) {
          ;[, status, error] = await createNewEmailConnection(submissionData)
        } else {
          ;[, status, error] = await api.registerConnection(
            accountId,
            projectId,
            submissionData
          )
        }
      } else {
        if (isEmailForm) {
          ;[, status, error] = await api.updateConnection(
            accountId,
            projectId,
            connection.connectionId,
            submissionData
          )
          if (!error) {
            const emailSubmissionData = createEmailSubmissionData(
              connection.connectionId,
              data
            )

            if (!emailSubmissionData) {
              return
            } else if (emailSubmissionData.length > 0) {
              ;[, status, error] = await api.updateConnectionInstruments(
                accountId,
                projectId,
                connection.connectionId,
                emailSubmissionData
              )
            }
          }
        } else {
          ;[, status, error] = await api.updateConnection(
            accountId,
            projectId,
            connection.connectionId,
            submissionData
          )
        }
      }

      if (error) {
        AntModal.error({
          title: 'Error Saving Connection',
          content: (
            <>
              <p>Error, you may wish to try again. </p>
              <p>{`Details: ${status}: ${error}`}</p>
            </>
          )
        })
        setSubmitting(false)
      } else {
        setSubmitting(false)
        navigate(
          generatePath(ROUTES.CONNECTIONS.LIST, {
            accountId: accountId,
            projectId: projectId
          })
        )
        notification.success({
          message: 'Successfully saved connection'
        })
      }
    },
    [
      accountId,
      api,
      connection.connectionId,
      draftItem,
      isEmailForm,
      isNew,
      navigate,
      projectId
    ]
  )

  function handleProviderDefinitionIdChange(value) {
    form.setValue('providerDefinitionId', value)
    if (
      value === PROVIDER_DEFINITION_ID.EMAIL_VALUE &&
      form.getValues('name') === defaultMobileName
    ) {
      form.setValue('name', defaultEmailName)
    } else if (
      value === PROVIDER_DEFINITION_ID.EMAIL_VALUE &&
      form.getValues('name') === defaultEmailName
    ) {
      form.setValue('name', defaultMobileName)
    }
    setIsEmailForm(value == PROVIDER_DEFINITION_ID.EMAIL_VALUE)
  }

  function handleIsChanged(value) {
    //Password only gets updated if the isChanged flag gets set. Actual value in control when isChanged is false is purely for cosmetic reasons
    if (value) {
      form.setValue('connPassword', '')
    } else {
      form.setValue('connPassword', '***********')
    }

    setIsCredentialsChanged(value)
  }

  const handleSubmit = form.handleSubmit(onSubmit)
  return (
    <FormProvider {...form}>
      <SectionProvider>
        <BodyContentColumn>
          <AntForm onFinish={handleSubmit} layout="vertical">
            <Section>
              {({ firstInputRef }) => (
                <>
                  <Typography.Title level={3}>
                    {isNew ? 'Create New Connection' : 'Edit Connection'}
                  </Typography.Title>
                  <FormItem
                    name="name"
                    label="Connection Name"
                    inputRef={firstInputRef}
                    rules={{
                      required: true,
                      minLength: 5
                    }}
                  />
                  <FormItem
                    name="providerDefinitionId"
                    label="Provider Definition Id"
                    rules={{
                      required: true
                    }}
                    disabled={!isNew}
                  >
                    <Select
                      mode="single"
                      onSelect={handleProviderDefinitionIdChange}
                      options={[
                        {
                          label: 'Email Monitor',
                          value: PROVIDER_DEFINITION_ID.EMAIL_VALUE
                        },
                        {
                          label: 'Mobile Qflow V1.0',
                          value: PROVIDER_DEFINITION_ID.MOBILE_VALUE
                        }
                      ]}
                    />
                  </FormItem>
                  {!isNew && !isEmailForm && (
                    <FormItem
                      name={`isChanged`}
                      label="Update Credentials"
                      defaultValue={false}
                    >
                      <Switch
                        onClick={handleIsChanged}
                        style={{ marginLeft: '1rem' }}
                      />
                    </FormItem>
                  )}
                  <FormItem
                    name="connUsername"
                    label="User Name"
                    hidden={isEmailForm}
                    rules={{
                      required: !isEmailForm
                    }}
                    disabled={!isCredetialsChanged}
                  />
                  <FormItem
                    name="connPassword"
                    label="Password"
                    hidden={isEmailForm}
                    rules={{
                      required: !isEmailForm
                    }}
                    disabled={!isCredetialsChanged}
                  >
                    <Password />
                  </FormItem>
                </>
              )}
            </Section>
            {fields.map((email, index) => (
              <div key={email.id}>
                <Section hidden={!isEmailForm}>
                  {({ firstInputRef }) => (
                    <>
                      <Typography.Title level={3}>Email</Typography.Title>
                      {email.instrumentId == Guid.Empty && index != 0 && (
                        <Button
                          type="primary"
                          disabled={submitting}
                          onClick={() => remove(index)}
                        >
                          {'Remove'}
                        </Button>
                      )}
                      <FormItem
                        name={`emails.${index}.instrumentId`}
                        defaultValue={email.instrumentId}
                        hidden={true}
                      />
                      <FormItem
                        name={`emails.${index}.feedId`}
                        defaultValue={email.feedId}
                        hidden={true}
                      />
                      {email.instrumentId != Guid.Empty && (
                        <FormItem
                          name={`emails.${index}.isDeleted`}
                          label="Delete Email?"
                          defaultValue={false}
                        >
                          <Switch style={{ marginLeft: '1rem' }} />
                        </FormItem>
                      )}
                      <FormItem
                        name={`emails.${index}.email`}
                        label="Email"
                        inputRef={firstInputRef}
                        defaultValue={email.email || '@qualisflow.com'}
                        rules={{
                          required: isEmailForm,
                          validate: isEmailForm
                            ? {
                                checkValue: v =>
                                  v.endsWith('@qualisflow.com') ||
                                  'ERROR: Email must end with @qualisflow.com',
                                inUse: v =>
                                  !allEmailAddresses.includes(v) ||
                                  'Error: This email address is already in use.'
                              }
                            : { checkValue: v => true }
                        }}
                      />
                      <FormItem
                        name={`emails.${index}.emailIdentifier`}
                        label="Email Identifier"
                        defaultValue={email.emailIdentifier}
                        rules={{
                          required: isEmailForm,
                          pattern: isEmailForm
                            ? {
                                value:
                                  /^[{(]?[0-9A-Fa-f]{8}-([0-9A-Fa-f]{4}-){3}[0-9A-Fa-f]{12}[)}]?$/,
                                message: 'Invalid'
                              }
                            : {},
                          validate: isEmailForm
                            ? {
                                // checkValue is required and runs on page load
                                // so we set this to always return true.
                                checkValue: v => true,
                                inUse: v =>
                                  !allEmailIdentifiers.includes(v) ||
                                  'Error: This email identifier is already in use.'
                              }
                            : { checkValue: v => true }
                        }}
                      />
                      <FormItem
                        name={`emails.${index}.moduleTagDefinitionId`}
                        label="Module"
                        defaultValue={email.moduleTagDefinitionId}
                        rules={{
                          required: isEmailForm
                        }}
                      >
                        <Select
                          mode="single"
                          showSearch
                          options={[
                            {
                              label: 'Waste',
                              value: MODULE_ID.WASTE
                            },
                            {
                              label: 'Delivery',
                              value: MODULE_ID.DELIVERY
                            }
                          ]}
                        />
                      </FormItem>
                      {/** Never changed by DAs */}
                      <FormItem
                        name={`emails.${index}.permittedFileTypes`}
                        label="Permitted File Types"
                        defaultValue={email.permittedFileTypes}
                        hidden={true}
                      />
                      {/** Never changed by DAs */}
                      <FormItem
                        name={`emails.${index}.permittedSendersPattern`}
                        label="Permitted Sender Pattern"
                        hidden={true}
                        defaultValue={email.permittedSendersPattern}
                      />
                    </>
                  )}
                </Section>
              </div>
            ))}
            <Row
              align="middle"
              justify={isEmailForm ? 'space-between' : 'center'}
            >
              <Button type="primary" htmlType="submit" disabled={submitting}>
                {isNew ? 'Create' : 'Update'}
              </Button>
              <Button
                hidden={!isEmailForm}
                type="primary"
                disabled={submitting || fields.length >= 2}
                onClick={() => append(blankEmail)}
              >
                {'Add Email'}
              </Button>
            </Row>
          </AntForm>
        </BodyContentColumn>
      </SectionProvider>
    </FormProvider>
  )
}

const blankEmail = {
  email: '',
  emailIdentifier: '',
  moduleTagDefinitionId: '',
  permittedFileTypes: '.pdf,.jpg,.jpeg,.png,.doc,.docx',
  permittedSendersPattern: '\\S+',
  isDeleted: false,
  feedId: Guid.Empty,
  instrumentId: Guid.Empty
}

const defaultMobileName = 'Qflow-mobile'
const defaultEmailName = 'Qflow-email'

const blankConnection = {
  name: defaultMobileName,
  connPassword: '',
  connUsername: '',
  providerDefinitionId: PROVIDER_DEFINITION_ID.MOBILE_VALUE,
  emails: [blankEmail]
}

EditConnection.propTypes = {
  connection: PropTypes.object,
  instruments: PropTypes.array,
  isNew: PropTypes.bool.isRequired,
  accountId: PropTypes.string.isRequired,
  projectId: PropTypes.string
}
